import {
  CallDto,
  FilterGroupDto,
} from '@act/shared/data-transfer-objects';
import {
  ActionReducerMapBuilder,
  PayloadAction,
} from '@reduxjs/toolkit';
import { resetRedux } from '../actions/sharedActions';
import { getCalls, getCallById } from '../thunks/calls.thunk';
import { getGuideInteractions } from './docketSlice.slice';
import { feedSlice, getFeedItems } from './feedSlice.slice';
import { CallState } from '../types/callsTypes';
import {
  callsAdapter,
  callsInitialState,
  upsertCallsPreservingRecording,
  voiceCallReviewAdapter,
} from './callsSliceState.util';
import {
  getActiveCalls,
  getCallFilters,
  getCallReviewTasks,
  getSuggestions,
  unassignCallSuccess,
  updateCallReview,
  updateSubjectId,
  updateSubjectIdSuccess,
} from './callsSliceThunks.thunk';

/**
 * Registers async and cross-slice reducers for the calls slice.
 * @param builder RTK extra reducer builder for calls state.
 */
export function addCallsSliceExtraReducers(
  builder: ActionReducerMapBuilder<CallState>
): void {
  builder
    .addCase(resetRedux, state => {
      state = { ...callsInitialState };
      return state;
    })
    .addCase(getCalls.pending, state => {
      state.calls.status = 'loading';
    })
    .addCase(getCalls.fulfilled, (state, action) => {
      const previousPageIds = state.callPageIds;
      const newIds = action.payload.calls.map(call => call.id);
      const idsToRemove = previousPageIds.filter(id => !newIds.includes(id));
      if (idsToRemove.length > 0) {
        callsAdapter.removeMany(state.calls, idsToRemove);
        // Drop stale detail hydration status with the entities. Otherwise
        // paging back inserts slim list stubs while status stays `success`,
        // and the review pane treats empty transcript fields as ready.
        for (const id of idsToRemove) {
          delete state.callDetailStatusById[id];
        }
      }

      upsertCallsPreservingRecording(state.calls, action.payload.calls);

      state.callPageTotalCount = action.payload.count;
      state.callPageIds = newIds;
      state.calls.status = 'success';
    })
    .addCase(getCalls.rejected, state => {
      state.calls.status = 'failed';
    })
    .addCase(getCallById.pending, (state, action) => {
      const callId = action.meta.arg?.params?.id;
      if (callId != null) {
        state.callDetailStatusById[callId] = 'loading';
      }
    })
    .addCase(getCallById.fulfilled, (state, action) => {
      const callId = action.meta.arg?.params?.id ?? action.payload?.id;
      upsertCallsPreservingRecording(state.calls, [action.payload]);
      if (callId != null) {
        state.callDetailStatusById[callId] = 'success';
      }
    })
    .addCase(getCallById.rejected, (state, action) => {
      const callId = action.meta.arg?.params?.id;
      if (callId != null) {
        state.callDetailStatusById[callId] = 'failed';
      }
    })
    .addCase(getFeedItems.fulfilled, (state, action) => {
      // Merge — addMany skips existing IDs, so slim GET /calls stubs would block
      // feed payloads that include voicemailTranscription / recording text.
      upsertCallsPreservingRecording(state.calls, action.payload.calls);
      state.calls.status = 'success';
    })
    .addCase(feedSlice.actions.feedItemUpdated, (state, action) => {
      if (!action.payload.feedItemModelView.calls) return;

      upsertCallsPreservingRecording(
        state.calls,
        action.payload.feedItemModelView.calls
      );
    })
    .addCase(getGuideInteractions.fulfilled, (state, action) => {
      upsertCallsPreservingRecording(state.calls, action.payload.calls);
    })
    .addCase(getCallFilters.pending, state => {
      state.callFilterOptionStatus = 'loading';
    })
    .addCase(
      getCallFilters.fulfilled,
      (state, action: PayloadAction<FilterGroupDto>) => {
        state.callFilterOptionStatus = 'success';
        state.callFilterOptions = action.payload;
      }
    )
    .addCase(getCallFilters.rejected, state => {
      state.callFilterOptionStatus = 'failed';
    })
    .addCase(updateSubjectId.pending, state => {
      state.calls.status = 'loading';
    })
    .addCase(
      updateSubjectId.fulfilled,
      (state, action: PayloadAction<CallDto>) => {
        state.calls.status = 'success';
        if (state.calls.entities[action.payload.id]) {
          state.calls.entities[action.payload.id] = {
            ...state.calls.entities[action.payload.id],
            ...action.payload,
          };
        }
        if (state.justInTimeCalls[action.payload.id]) {
          state.justInTimeCalls[action.payload.id] = {
            ...state.justInTimeCalls[action.payload.id],
            ...action.payload,
          };
        }
      }
    )
    .addCase(updateSubjectIdSuccess, (state, action) => {
      if (state.calls.entities[action.payload.id]) {
        state.calls.entities[action.payload.id] = {
          ...state.calls.entities[action.payload.id],
          ...action.payload,
        };
      }
      if (state.justInTimeCalls[action.payload.id]) {
        state.justInTimeCalls[action.payload.id] = {
          ...state.justInTimeCalls[action.payload.id],
          ...action.payload,
        };
      }
      state.calls.status = 'success';
    })
    .addCase(updateSubjectId.rejected, state => {
      state.calls.status = 'failed';
    })
    .addCase(unassignCallSuccess, (state, action: PayloadAction<CallDto>) => {
      const call = action.payload;
      if (state.calls.entities[call.id]) {
        state.calls.entities[call.id] = {
          ...state.calls.entities[call.id],
          ...call,
        };
      }
      if (state.justInTimeCalls[call.id]) {
        state.justInTimeCalls[call.id] = {
          ...state.justInTimeCalls[call.id],
          ...call,
        };
      }
      state.calls.status = 'success';
    })
    .addCase(getCallReviewTasks.pending, state => {
      state.voiceCallReviews.status = 'loading';
    })
    .addCase(getCallReviewTasks.fulfilled, (state, action) => {
      voiceCallReviewAdapter.addMany(state.voiceCallReviews, action.payload);
      state.voiceCallReviews.status = 'success';
    })
    .addCase(getCallReviewTasks.rejected, state => {
      state.voiceCallReviews.status = 'failed';
    })
    .addCase(updateCallReview.fulfilled, (state, action) => {
      voiceCallReviewAdapter.upsertOne(
        state.voiceCallReviews,
        action.payload
      );
    })
    .addCase(getActiveCalls.fulfilled, (state, action) => {
      action.payload.forEach(call => {
        state.justInTimeCalls[call.id] = call;
      });
      callsAdapter.upsertMany(state.calls, action.payload);
    })
    .addCase(getSuggestions.pending, (state, action) => {
      const callId = action.meta.arg?.params?.id;
      if (callId) {
        state.suggestionsLoadingStatus[callId] = 'loading';
      }
    })
    .addCase(getSuggestions.fulfilled, (state, action) => {
      const callId = action.meta.arg?.params?.id;
      if (callId) {
        const filteredPayload = {
          ...action.payload,
          actions: action.payload?.actions?.filter(
            suggestion => !(suggestion?.tempNeedId && !suggestion?.needId)
          ),
        };
        state.suggestions[callId] = filteredPayload;
        state.suggestionsLoadingStatus[callId] = 'success';
      }
    })
    .addCase(getSuggestions.rejected, (state, action) => {
      const callId = action.meta.arg?.params?.id;
      if (callId) {
        state.suggestionsLoadingStatus[callId] = 'failed';
      }
    });
}
